import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';
import type { GenerationJob } from '@muidsoft/shared';

/**
 * useJobListPolling — poll GET /api/bff/jobs?productId=&limit= secara periodik.
 *
 * Algoritma interval:
 * - Ada job aktif (non-terminal)?  → poll setiap 5 detik
 * - Semua terminal (atau kosong)?  → poll setiap 30 detik (hemat, tapi tetap
 *   refresh kalau user buka tab ini setelah lama)
 *
 * Expose `refresh()` untuk force-poll segera (misal setelah retry/delete).
 */

const ACTIVE_INTERVAL_MS  = 5_000;
const IDLE_INTERVAL_MS    = 30_000;
const ERROR_INTERVAL_MS   = 10_000;

const TERMINAL_STEPS = new Set(['completed', 'failed']);

function hasActiveJob(jobs: GenerationJob[]): boolean {
  return jobs.some((j) => !TERMINAL_STEPS.has(j.currentStep));
}

interface UseJobListPollingOptions {
  productId: string | null;
  limit?: number;
  /** cursor untuk pagination awal; biasanya null (halaman pertama) */
  cursor?: string | null;
}

export function useJobListPolling({ productId, limit = 20 }: UseJobListPollingOptions) {
  const [jobs, setJobs]           = useState<GenerationJob[]>([]);
  const [meta, setMeta]           = useState<{ nextCursor: string | null }>({ nextCursor: null });
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef  = useRef<(() => Promise<void>) | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!productId) {
      setJobs([]);
      setMeta({ nextCursor: null });
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    function clearTimer() {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }

    function scheduleNext(intervalMs: number) {
      if (cancelled) return;
      timerRef.current = setTimeout(poll, intervalMs);
    }

    async function poll() {
      try {
        const qs = new URLSearchParams({ productId: productId!, limit: String(limit) });
        const res = await api.get<{ data: GenerationJob[]; meta: { nextCursor: string | null } }>(
          `/api/bff/jobs?${qs}`,
        );
        if (cancelled) return;

        setJobs(res.data);
        setMeta(res.meta);
        setError(null);

        const interval = hasActiveJob(res.data) ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS;
        scheduleNext(interval);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Gagal memuat jobs');
        scheduleNext(ERROR_INTERVAL_MS);
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    }

    pollRef.current = poll;
    setLoading(true);
    poll();

    return () => {
      cancelled = true;
      pollRef.current = null;
      clearTimer();
    };
  }, [productId, limit]);

  /** Force-poll segera — panggil setelah retry/delete */
  const refresh = useCallback(() => {
    if (!pollRef.current) return;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pollRef.current();
  }, []);

  /** Append halaman berikutnya (untuk tombol "Muat Lebih Banyak") */
  const loadMore = useCallback(async (cursor: string) => {
    if (!productId) return;
    const qs = new URLSearchParams({ productId: productId, limit: String(limit), cursor });
    try {
      const res = await api.get<{ data: GenerationJob[]; meta: { nextCursor: string | null } }>(
        `/api/bff/jobs?${qs}`,
      );
      setJobs((prev) => [...prev, ...res.data]);
      setMeta(res.meta);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memuat lebih banyak');
    }
  }, [productId, limit]);

  return { jobs, meta, loading, error, refresh, loadMore };
}
